import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import type { IntlShape } from "react-intl";

import { MESSAGES } from "./message-definitions";

dayjs.extend(utc);

export type CoverageGapRange = { start: Dayjs; end: Dayjs; openEnded?: boolean };

export type CoverageGapSpan = { start: Dayjs; end: Dayjs };

export type CoverageGapText =
  | { kind: "text"; text: string }
  | { kind: "fragmentedList"; intro: string; periods: string[]; outro: string };

const formatGapDate = (date: Dayjs, locale: string): string =>
  new Intl.DateTimeFormat(locale.startsWith("en") ? "en-GB" : locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date.toDate());

export const formatCoverageGapPeriod = (intl: IntlShape, { start, end, openEnded }: CoverageGapRange): string => {
  if (openEnded) {
    return intl.formatMessage(MESSAGES.datesOpen, { startDate: formatGapDate(start, intl.locale) });
  }
  return start.isSame(end, "day")
    ? formatGapDate(start, intl.locale)
    : intl.formatMessage(MESSAGES.dates, {
        startDate: formatGapDate(start, intl.locale),
        endDate: formatGapDate(end, intl.locale),
      });
};

const airCalendarDay = (value: string): Dayjs => dayjs(dayjs.utc(value).format("YYYY-MM-DD"));

export const getAirPeriodRanges = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  now: Dayjs = dayjs()
): CoverageGapRange[] => {
  const yesterday = airCalendarDay(now.toISOString()).subtract(1, "day");
  return periods.flatMap((period) => {
    const start = airCalendarDay(period.start);
    const end = period.end ? airCalendarDay(period.end) : yesterday;
    return start.isAfter(end, "day") ? [] : [{ start, end, openEnded: !period.end }];
  });
};

export const getAirPeriodSpan = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  now: Dayjs = dayjs()
): CoverageGapSpan | undefined => {
  const ranges = getAirPeriodRanges(periods, now);
  if (ranges.length === 0) {
    return undefined;
  }
  return {
    start: ranges.reduce(
      (earliest, range) => (range.start.isBefore(earliest, "day") ? range.start : earliest),
      ranges[0].start
    ),
    end: ranges.reduce((latest, range) => (range.end.isAfter(latest, "day") ? range.end : latest), ranges[0].end),
  };
};

const isLeadingGap = (range: CoverageGapRange, span: CoverageGapSpan | undefined): boolean =>
  span !== undefined && range.start.isSame(span.start, "day") && range.end.isBefore(span.end, "day");

const getSingleGapText = (intl: IntlShape, range: CoverageGapRange, span: CoverageGapSpan | undefined): string => {
  if (range.openEnded) {
    return intl.formatMessage(MESSAGES.openGap, { date: formatGapDate(range.start, intl.locale) });
  }
  if (isLeadingGap(range, span)) {
    return intl.formatMessage(MESSAGES.leadingGap, { date: formatGapDate(range.end.add(1, "day"), intl.locale) });
  }
  return intl.formatMessage(MESSAGES.closedGap, {
    startDate: formatGapDate(range.start, intl.locale),
    endDate: formatGapDate(range.end, intl.locale),
  });
};

export const getCoverageGapText = (
  intl: IntlShape,
  ranges: CoverageGapRange[],
  span?: CoverageGapSpan
): CoverageGapText => {
  if (ranges.length === 0) {
    return { kind: "text", text: intl.formatMessage(MESSAGES.complete) };
  }
  if (ranges.length === 1) {
    return { kind: "text", text: getSingleGapText(intl, ranges[0], span) };
  }
  return {
    kind: "fragmentedList",
    intro: intl.formatMessage(MESSAGES.fragmentedIntro, { count: ranges.length }),
    periods: ranges.map((range) => formatCoverageGapPeriod(intl, range)),
    outro: intl.formatMessage(MESSAGES.fragmentedOutro),
  };
};

export const getCoverageGapSentence = (intl: IntlShape, ranges: CoverageGapRange[], span?: CoverageGapSpan): string => {
  const body = getCoverageGapText(intl, ranges, span);
  return body.kind === "text" ? body.text : `${body.intro} ${body.periods.join("; ")}. ${body.outro}`;
};
